import { useState, type ReactNode } from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Compass,
  LayoutDashboard,
  LogOut,
  User,
  UserRound,
  type LucideIcon,
} from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { useOnExplore } from '@/hooks/useOnExplore'
import {
  ADMIN_SIGN_IN_PATH,
  CREATE_PAGE_PATH,
  CREATOR_PROFILE_PATH,
  CREATOR_SIGN_IN_PATH,
  CREATOR_SIGN_OUT_PATH,
} from './publicShell'
import type { CreatorAuthProp } from '@/types/creator'

/**
 * O ícone de pessoa do canto do header, agora com menu.
 *
 * Existe porque a BeWave tem DUAS contas, e antes o ícone levava direto para a
 * do admin: quem quisesse entrar como criador de página precisava adivinhar o
 * caminho pelo Explore → "Criar sua página". As duas portas agora aparecem
 * lado a lado, sem sair da página em que a pessoa está.
 *
 * Logado como criador, o mesmo botão vira o avatar da conta — nome, e-mail,
 * perfil e sair. `creator_auth` é shared prop (CreatorAuthentication) e vem em
 * toda página pública; onde ela não vier (a tela de login do admin, por
 * exemplo) o menu simplesmente mostra as portas de entrada.
 *
 * Duas coisas dependem de ONDE o menu está (`useOnExplore`): "Minhas páginas"
 * não mora mais aqui — é link da barra — e a porta do BeWave Admin não aparece
 * no Explore, que é outro produto, em outro domínio, e não o que a pessoa foi
 * procurar no diretório.
 */
export default function PublicUserMenu({
  hideExplore = false,
}: {
  /**
   * Esconde a porta "BeWave Explore" e o convite "Criar conta" — só a landing
   * comercial usa isto. Não afeta quem já está logado como criador: a home
   * nem compartilha `creator_auth` (ver home_controller.rb), então esse
   * estado já não aparece por lá.
   */
  hideExplore?: boolean
} = {}) {
  const [open, setOpen] = useState(false)
  const { creator_auth } = usePage<{ creator_auth?: CreatorAuthProp }>().props
  const creator = creator_auth?.creator ?? null
  const onExplore = useOnExplore()

  const close = () => setOpen(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={creator ? 'Sua conta' : 'Entrar'}
          className="inline-flex size-10 items-center justify-center rounded-full text-gray-800 transition-colors hover:bg-gray-100"
        >
          {creator ? (
            // Iniciais, e não foto: a conta de criador ainda não tem avatar.
            <span className="inline-flex size-8 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
              {initials(creator.first_name, creator.last_name)}
            </span>
          ) : (
            <User className="size-5" aria-hidden="true" />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-72 p-2">
        {creator ? (
          <>
            <div className="flex items-center gap-3 px-2 py-2">
              <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                {initials(creator.first_name, creator.last_name)}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900">
                  {creator.first_name} {creator.last_name}
                </p>
                <p className="truncate text-xs text-gray-500">
                  {creator.email}
                </p>
              </div>
            </div>

            <Separator className="my-2" />

            {/* "Minhas páginas" saiu daqui: virou link da barra (PublicHeader),
                onde não depende de ninguém descobrir que o avatar abre menu. */}
            {/* O perfil é da CONTA, não de uma página: quem tem três páginas se
                apresenta uma vez só, e é esse texto que entra no bloco "quem
                atende" de todas elas. */}
            <MenuLink
              href={CREATOR_PROFILE_PATH}
              icon={UserRound}
              label="Meu perfil"
              onClick={close}
            />
            {/* Fora do Explore, a outra porta continua à mão: ter conta de
                criador não impede ninguém de também ser dono de escola. */}
            {!onExplore && (
              <MenuLink
                href={ADMIN_SIGN_IN_PATH}
                icon={LayoutDashboard}
                label="BeWave Admin"
                onClick={close}
              />
            )}

            <Separator className="my-2" />

            <button
              type="button"
              onClick={() => {
                close()
                router.delete(CREATOR_SIGN_OUT_PATH)
              }}
              className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left text-sm font-semibold text-red-600 transition-colors hover:bg-red-50"
            >
              <LogOut className="size-4 shrink-0" aria-hidden="true" />
              Sair
            </button>
          </>
        ) : (
          <>
            <p className="px-2 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Entrar em
            </p>

            {/* No Explore não se entra no painel da escola: quem chega ali está
                no diretório, e a porta do admin mandava para o outro produto —
                em outro domínio — sem ninguém ter pedido. */}
            {!onExplore && (
              <MenuLink
                href={ADMIN_SIGN_IN_PATH}
                icon={LayoutDashboard}
                label="BeWave Admin"
                hint="O painel da sua escola"
                onClick={close}
              />
            )}
            {!hideExplore && (
              <MenuLink
                href={CREATOR_SIGN_IN_PATH}
                icon={Compass}
                label="BeWave Explore"
                hint="Sua página no diretório"
                onClick={close}
              />
            )}

            {!hideExplore && (
              <>
                <Separator className="my-2" />

                <a
                  href={CREATE_PAGE_PATH}
                  onClick={close}
                  className="block rounded-lg px-2 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
                >
                  Ainda não tem página?{' '}
                  <span className="font-semibold text-primary">
                    Criar conta
                  </span>
                </a>
              </>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}

/** Uma linha do menu. `hint` é a segunda linha, que explica para onde leva. */
function MenuLink({
  href,
  icon: Icon,
  label,
  hint,
  onClick,
  className,
}: {
  href: string
  icon: LucideIcon
  label: ReactNode
  hint?: string
  onClick?: () => void
  className?: string
}) {
  return (
    <a
      href={href}
      onClick={onClick}
      className={cn(
        'flex items-start gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-gray-50',
        className,
      )}
    >
      <Icon
        className="mt-0.5 size-4 shrink-0 text-gray-500"
        aria-hidden="true"
      />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-gray-900">
          {label}
        </span>
        {hint && <span className="block text-xs text-gray-500">{hint}</span>}
      </span>
    </a>
  )
}

/**
 * Iniciais do criador. Só o primeiro caractere de cada nome, em caixa alta —
 * `charAt` e não `[0]` porque um nome vazio devolveria `undefined` e a string
 * sairia com "undefined" no meio.
 */
function initials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
}
